import React, { useEffect, useState } from "react";
import { Check, Loader2, Circle, AlertCircle } from "lucide-react";

/**
 * ProcessingProgress
 * ------------------
 * Honest, staged progress panel used across Intuiterview long-running
 * operations (resume analyze, resume align, resume export, etc.).
 *
 * Contract:
 *   - `stages`: static list of { key, label } - the meaningful stages
 *     of the actual operation. Every stage listed here MUST correspond
 *     to real backend work.
 *   - `currentStage`: the `stage` field returned by the polling API.
 *     Stages appearing BEFORE this one in the list render as complete;
 *     the matching one renders as current (with a subtle spinner);
 *     later ones render as pending.
 *   - `status`: "processing" | "ready" | "failed" | "cancelled".
 *   - `title`: overall operation name ("Analyzing your resume", etc.)
 *   - `errorMessage`: shown when `status === "failed"`.
 *   - `onCancel` / `onRetry`: optional handlers.
 *   - `startedAt`: ISO timestamp - enables an honest elapsed-time
 *     counter. NO fake percentages, NO fake countdowns.
 *
 * Design: matches the app's dark/premium/gold identity. Vertical step
 * list on any screen ≥ 375px. Fully accessible (aria-live for stage
 * transitions, aria-current, focus target on retry).
 */
export default function ProcessingProgress({
  title,
  subtitle,
  stages,
  currentStage,
  status = "processing",
  errorMessage,
  onCancel,
  onRetry,
  startedAt,
  compact = false,
  testId = "processing-progress",
}) {
  const [elapsedSec, setElapsedSec] = useState(0);

  // Honest elapsed-time counter. Reflects the actual work done -
  // never counts down, never claims a percentage.
  useEffect(() => {
    if (status !== "processing" || !startedAt) return;
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsedSec(Math.max(0, Math.round((Date.now() - start) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [startedAt, status]);

  const currentIdx = stages.findIndex((s) => s.key === currentStage);
  const completedCount =
    status === "ready"
      ? stages.length
      : currentIdx < 0
      ? 0
      : currentIdx;

  // ── FAILED ────────────────────────────────────────────────────────────────
  if (status === "failed") {
    return (
      <div
        data-testid={`${testId}-failed`}
        role="alert"
        className="rounded-xl border border-rose-500/25 bg-rose-500/[0.05] p-5"
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 flex-shrink-0 rounded-full bg-rose-500/10 border border-rose-500/25 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-rose-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-zinc-50">
              We couldn&apos;t finish{title ? `, ${title.toLowerCase()}` : ""}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              {errorMessage ||
                "Something went wrong. Your work has not been lost. Please try again in a moment."}
            </div>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                data-testid={`${testId}-retry-btn`}
                className="mt-3 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-md px-3 py-1.5 text-sm transition-colors"
              >
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── CANCELLED ────────────────────────────────────────────────────────────
  if (status === "cancelled") {
    return (
      <div
        data-testid={`${testId}-cancelled`}
        className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-5 text-sm text-zinc-400"
      >
        Cancelled. You can start again whenever you&apos;re ready.
      </div>
    );
  }

  // ── PROCESSING / READY ───────────────────────────────────────────────────
  return (
    <div
      data-testid={testId}
      role="status"
      aria-live="polite"
      className={`rounded-xl border border-amber-500/20 bg-zinc-950/60 ${
        compact ? "p-4" : "p-5"
      } space-y-4`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
          {status === "ready" ? (
            <Check className="h-4 w-4 text-emerald-400" />
          ) : (
            <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-semibold text-zinc-50"
            data-testid={`${testId}-title`}
          >
            {status === "ready" ? title?.replace(/…$/, "") + ": complete" : title}
          </div>
          {subtitle && (
            <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>
          )}
        </div>
        <div
          className="flex items-center gap-3 text-[11px] text-zinc-500 tabular-nums"
          data-testid={`${testId}-meta`}
        >
          <span data-testid={`${testId}-step-counter`}>
            {Math.min(completedCount, stages.length)} of {stages.length}
          </span>
          {status === "processing" && startedAt && (
            <span data-testid={`${testId}-elapsed`}>{formatElapsed(elapsedSec)}</span>
          )}
        </div>
      </div>

      {/* Vertical step list, completed / current / pending */}
      <ol
        className="space-y-2 pt-1"
        data-testid={`${testId}-stage-list`}
      >
        {stages.map((s, i) => {
          const isComplete = status === "ready" || i < currentIdx;
          const isCurrent = status === "processing" && i === currentIdx;
          return (
            <li
              key={s.key}
              aria-current={isCurrent ? "step" : undefined}
              data-testid={`${testId}-stage-${s.key}`}
              data-state={isComplete ? "complete" : isCurrent ? "current" : "pending"}
              className={`flex items-start gap-2.5 text-sm ${
                isComplete
                  ? "text-emerald-300"
                  : isCurrent
                  ? "text-zinc-50"
                  : "text-zinc-600"
              }`}
            >
              <span className="flex-shrink-0 mt-[3px] h-4 w-4 flex items-center justify-center">
                {isComplete ? (
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                ) : isCurrent ? (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
                  </span>
                ) : (
                  <Circle className="h-2 w-2 text-zinc-700 fill-zinc-800" />
                )}
              </span>
              <span className={isCurrent ? "font-medium" : ""}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      {onCancel && status === "processing" && (
        <div className="pt-2 border-t border-zinc-800/60 flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            data-testid={`${testId}-cancel-btn`}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-rose-200 hover:bg-rose-500/[0.08] border border-transparent hover:border-rose-500/30 rounded-md px-2.5 py-1 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function formatElapsed(sec) {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}
